/**
 * Store actions → outbox items (plan D18–D23, contracts §1).
 *
 * The outbox is T21's (`@repo/client/offline`) and T11 runs it
 * (`src/data/outbox-runner.ts`). This file only says WHAT each workout write
 * is on the wire: its kind, the entity it names, the session it waits on,
 * and a body the contract schema accepts exactly (the tests parse every body
 * with `sessionStartInput`, `recordSetInput`, `correctSetInput` and
 * `sessionEndInput`).
 *
 * ── ORDER IS THE OUTBOX'S, AND IT IS SET HERE ──────────────────────────────
 * Every write but the start declares `dependsOn: sessionId`, which makes the
 * session the unit of FIFO: the start goes before its sets, a correction or a
 * delete after the set it names, and the end after every set enqueued before
 * it. Enqueue order is call order, so every action enqueues SYNCHRONOUSLY —
 * never after an `await` — and two taps in one tick keep their order.
 *
 * The ONE write behind an `await` is the undo's DELETE: `remove` enqueues it
 * only once `withdrawUnsent` has answered. Both are hops on the outbox store's
 * single write chain, so the DELETE still lands behind the set's own writes.
 * What can slip into that gap is another action's enqueue — a `logSet` tapped
 * meanwhile queues a record for a DIFFERENT set, and order within the session
 * does not care where that one sits.
 *
 * ── ENQUEUE THROUGH THE RUNNER ─────────────────────────────────────────────
 * `useOutbox().enqueue` / `runner.enqueue`, never `store.enqueue`: only the
 * runner's enqueue triggers a drain (T11's Result).
 *
 * ── TIMES ARE THE CLIENT'S ─────────────────────────────────────────────────
 * `startedAt`, `performedAt` and `endedAt` are always sent (D20). A start
 * replayed an hour late without `startedAt` would be stamped an hour late, and
 * the server answers a replay with what it stored first.
 */
import type { OutboxEntry, OutboxItem } from "@repo/client/offline";
import type {
  CorrectSetInput,
  RecordSetInput,
  SessionEndInput,
  SessionStartInput,
} from "@repo/contract/training";
import { useContext, useMemo } from "react";
import { OutboxContext, type OutboxRunner } from "../../../data/outbox-runner";
import {
  type LoggedWorkoutSet,
  pausedSecondsAt,
  type SetCorrection,
  type WorkoutSession,
} from "./session-model";

const iso = (ms: number) => new Date(ms).toISOString();

// ── Entries ──────────────────────────────────────────────────────────────────

/**
 * The start of a session. A `Pick`, so the finished-workout log
 * (`features/logbook`) queues the same body for a session that never lived in
 * the store.
 */
export function startEntry(
  session: Pick<
    WorkoutSession,
    "planSessionId" | "readiness" | "sessionId" | "startedAt"
  >,
  /** Held until then, or until released (`D194`, the readiness check). */
  holdUntil?: Date
): OutboxEntry {
  const body: SessionStartInput = {
    id: session.sessionId,
    planSessionId: session.planSessionId,
    ...(session.readiness === null ? {} : { readiness: session.readiness }),
    startedAt: iso(session.startedAt),
  };
  return {
    body,
    dependsOn: null,
    entityId: session.sessionId,
    kind: "session.start",
    ...(holdUntil === undefined ? {} : { notBefore: holdUntil.toISOString() }),
  };
}

/**
 * A measure the set does not carry is left OUT of the body rather than sent
 * as `null`, except `load`, whose `null` means bodyweight and must arrive.
 *
 * `kind` and `side` (`D209`) are left out on a working set on both sides, so
 * that body is byte-for-byte what it was before the fields existed: an API
 * that predates them refuses an unknown key with 422 (`recordSetInput` is
 * strict), and only a warm-up, drop, failure or one-sided set names one.
 * A replay is the same body under the same id, which the server answers
 * with the stored row (`D19`).
 */
export function recordBody(set: LoggedWorkoutSet): RecordSetInput {
  return {
    exerciseId: set.exerciseId,
    id: set.id,
    load: set.load,
    performedAt: iso(set.performedAt),
    ...(set.reps === null ? {} : { reps: set.reps }),
    ...(set.rpe === null ? {} : { rpe: set.rpe }),
    ...(set.durationSeconds === null
      ? {}
      : { durationSeconds: set.durationSeconds }),
    ...(set.distanceMetres === null
      ? {}
      : { distanceMetres: set.distanceMetres }),
    ...(set.kind === undefined ? {} : { kind: set.kind }),
    ...(set.side === undefined ? {} : { side: set.side }),
  };
}

export function recordEntry(
  sessionId: string,
  set: LoggedWorkoutSet
): OutboxEntry {
  return {
    body: recordBody(set),
    dependsOn: sessionId,
    entityId: set.id,
    kind: "set.record",
  };
}

/**
 * `PUT /me/sets/:previousId` with the replacement's own id. Only the fields
 * the person changed travel: omitted is "carried forward" on this route, and
 * `null` is "cleared" (contract `correctSetInput`).
 */
export function correctEntry(
  sessionId: string,
  previousId: string,
  set: LoggedWorkoutSet,
  values: SetCorrection
): OutboxEntry {
  const body: CorrectSetInput = { id: set.id };
  for (const key of Object.keys(values) as (keyof SetCorrection)[]) {
    const value = values[key];
    if (value !== undefined) {
      Object.assign(body, { [key]: value });
    }
  }
  return {
    body,
    dependsOn: sessionId,
    entityId: previousId,
    kind: "set.correct",
  };
}

export function deleteEntry(sessionId: string, setId: string): OutboxEntry {
  return {
    body: null,
    dependsOn: sessionId,
    entityId: setId,
    kind: "set.delete",
  };
}

/**
 * The end of a session — a `Pick` for the same reason as {@link startEntry}.
 *
 * `pausedSeconds` (`D245`) is the TOTAL paused inside the session's span,
 * and is left out when there was no pause, so an end that never paused is
 * byte-for-byte the body it was: an API older than the field refuses the
 * key with 422. A replay resends the same total, which the server keeps
 * from the first ending that landed.
 */
export function endEntry(
  session: Pick<WorkoutSession, "ended" | "endedAt" | "sessionId"> &
    Partial<Pick<WorkoutSession, "pauses" | "startedAt">>
): OutboxEntry {
  const paused =
    session.endedAt === null || session.startedAt === undefined
      ? 0
      : pausedSecondsAt(
          { pauses: session.pauses, startedAt: session.startedAt },
          session.endedAt
        );
  const body: SessionEndInput = {
    ...(session.endedAt === null ? {} : { endedAt: iso(session.endedAt) }),
    ...(session.ended === null ? {} : { endReason: session.ended }),
    ...(paused > 0 ? { pausedSeconds: paused } : {}),
  };
  return {
    body,
    dependsOn: session.sessionId,
    entityId: session.sessionId,
    kind: "session.end",
  };
}

// ── The port the store writes through ────────────────────────────────────────

/** The runner's surface this file needs; `useWorkoutOutboxPort` binds it. */
export interface OutboxPort {
  /** The RUNNER's: it drops the quarantined item and triggers a drain. */
  discard(id: string): Promise<void>;
  enqueue(entry: OutboxEntry): Promise<OutboxItem | unknown>;
  /**
   * The RUNNER's batch: every entry in ONE store write, then one drain. What
   * {@link WorkoutOutbox.recordAndEnd} needs so a kill cannot keep the day's
   * last set and lose its session's end (`T-196`).
   */
  enqueueMany(entries: readonly OutboxEntry[]): Promise<unknown>;
  /**
   * Ask the runner for a drain. Needed because `withdrawUnsent` is the STORE's
   * and triggers nothing, while what it removes may be the quarantined write
   * that was holding its session's whole stream.
   */
  trigger(): void;
  /**
   * T27's all-or-nothing cancel, decided ON the store's write chain, so it
   * cannot race the drain the way a plain `dropEntity` does. `entityIds[0]`
   * is the ORIGIN — the id the `set.record` carries.
   */
  withdrawUnsent(entityIds: readonly string[]): Promise<boolean>;
}

/** What the store's actions call. Each returns once the write is persisted. */
export interface WorkoutOutbox {
  correct(
    sessionId: string,
    previousId: string,
    set: LoggedWorkoutSet,
    values: SetCorrection
  ): Promise<void>;
  /** Drop one quarantined write the person discarded (the runner drains). */
  discard(id: string): Promise<void>;
  end(session: WorkoutSession): Promise<void>;
  /**
   * Every queued write of a set the person DISCARDED from the quarantine
   * sheet, withdrawn in one all-or-nothing call (T27): the refused record and
   * any correction or delete behind it, none of which was ever sent, because
   * the quarantined record held the session's stream.
   *
   * `false` means the record is no longer queued — someone discarded it
   * already — and the caller then drops the item itself.
   */
  forget(set: LoggedWorkoutSet): Promise<boolean>;
  record(sessionId: string, set: LoggedWorkoutSet): Promise<void>;
  /**
   * The set that completed the day and the session's end behind it, as ONE
   * outbox write: set first, then end. Two writes let a kill between them
   * keep the set, lose the end, and leave the session open on the server
   * while the device believes it finished (`T-196`).
   */
  recordAndEnd(
    sessionId: string,
    set: LoggedWorkoutSet,
    session: WorkoutSession
  ): Promise<void>;
  /**
   * Taking an undone set back: WITHDRAWN when every write of the set is
   * certainly not on the server (T27's `withdrawUnsent` — never attempted, or
   * quarantined because the server refused it); otherwise a DELETE behind
   * them, because the row may exist.
   *
   * Network state is not part of that question any more. It used to be: a
   * pending item could be picked by a drain in the very next microtask, since
   * the runner reads `nextReady` from a snapshot BEFORE its `markInFlight`
   * reaches the write chain — so an undo that merely dropped the item lost to
   * a send that went out anyway, and the device forgot a set the server
   * holds. Online the undo therefore always paid for a POST and a DELETE.
   * T27 moved the decision onto that same chain and made the runner re-check
   * the item after claiming it, so exactly one of the two wins.
   *
   * D23 still holds: what a withdraw removes is what the person's own undo
   * cancels, and only what the server never saw.
   */
  remove(
    sessionId: string,
    set: LoggedWorkoutSet
  ): Promise<"withdrawn" | "deleted">;
  start(session: WorkoutSession, holdUntil?: Date): Promise<void>;
}

export function createWorkoutOutbox(port: OutboxPort): WorkoutOutbox {
  const put = async (entry: OutboxEntry) => {
    await port.enqueue(entry);
  };
  return {
    correct: (sessionId, previousId, set, values) =>
      put(correctEntry(sessionId, previousId, set, values)),
    discard: (id) => port.discard(id),
    end: (session) => put(endEntry(session)),
    async forget(set) {
      const withdrawn = await port.withdrawUnsent([...set.supersedes, set.id]);
      if (withdrawn) {
        // The store's `withdrawUnsent` triggers nothing, and what just left
        // the queue was holding this session's later writes — its end, its
        // other sets. Without this they wait for an unrelated trigger.
        port.trigger();
      }
      return withdrawn;
    },
    record: (sessionId, set) => put(recordEntry(sessionId, set)),
    async recordAndEnd(sessionId, set, session) {
      await port.enqueueMany([recordEntry(sessionId, set), endEntry(session)]);
    },
    async remove(sessionId, set) {
      // Every id the set ever had, ORIGIN FIRST: its record, each correction
      // (whose entityId is the row it corrected) and anything naming the
      // latest. `supersedes[0]` is the id the `set.record` was queued under,
      // and T27 withdraws nothing unless THAT record is still queued — once
      // it synced, the row exists and only a DELETE can undo it.
      const withdrawn = await port.withdrawUnsent([...set.supersedes, set.id]);
      if (withdrawn) {
        // Same reason as `forget`: the withdrawn write may have been the
        // QUARANTINED one, and everything queued behind it is now free to go
        // with nothing to wake the runner (T33). The `deleted` path below
        // needs no trigger — the runner's own `enqueue` is one.
        port.trigger();
        return "withdrawn";
      }
      await put(deleteEntry(sessionId, set.id));
      return "deleted";
    },
    start: (session, holdUntil) => put(startEntry(session, holdUntil)),
  };
}

/**
 * The app's port: the runner T11 provides (`OutboxContext`). `enqueue` and
 * `discard` are the RUNNER's, because only those trigger a drain;
 * `withdrawUnsent` is the store's, because it must not — `trigger` is how its
 * callers ask for one when they know they unblocked something.
 */
export function useWorkoutOutboxPort(): OutboxPort {
  const runner = useContext(OutboxContext);
  if (runner === null) {
    throw new Error("The workout store needs DataProvider's outbox");
  }
  return useMemo(() => outboxPortOf(runner), [runner]);
}

/** {@link useWorkoutOutboxPort} outside React: the runtime's own actions. */
export function outboxPortOf(runner: OutboxRunner): OutboxPort {
  return {
    discard: (id: string) => runner.discard(id),
    enqueue: (entry: OutboxEntry) => runner.enqueue(entry),
    enqueueMany: (entries: readonly OutboxEntry[]) =>
      runner.enqueueMany(entries),
    trigger: () => runner.trigger(),
    withdrawUnsent: (entityIds: readonly string[]) =>
      runner.store.withdrawUnsent(entityIds),
  };
}
